// moms-harness:anchor v2
// Spec: docs/spec/settlement/SPEC-settlement-mwp-mlt-sku-attribute-registration-A.yaml
// JIRA: LOGISPRD-13286, LOGISPRD-13852, LOGISPRD-14470, LOGISPRD-14586, LOGISPRD-14591, LOGISPRD-14923
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import '@musinsa/pbo-core/tokens.css'
import '@musinsa/pbo-core/styles.css'
import '@musinsa/pbo-core/fonts.css'
import './skuAttributeManagement.css'
import { ConfigProvider, message } from '@musinsa/pbo-core'
import { pboLogisticsTheme } from '@musinsa/pbo-core/theme'
import PageHeader from '@/components/pbo/PageHeader'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { SkuAttributeFilter } from './components/SkuAttributeFilter'
import { SkuAttributeTable } from './components/SkuAttributeTable'
import type { ShipperOptionSource } from '@/components/system/shipperSelectFilter'
import { ShipperInfoMap, ShipperOptionItem, SkuAttributeParams, useGetShipperOptions } from './queries/useGetSkuAttributeList'
import { fromQuery } from './components/fromQuery'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'
import { shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { useListView } from './hooks/useListView'

/** 화주 목록이 없을 때의 빈 배열 — 렌더마다 새 배열이면 아래 메모와 필터의 화주 계산이 매번 다시 돈다 */
const NO_SHIPPERS: ShipperOptionItem[] = []

/**
 * MA18 신규 상품관리 — `sku_attribute` 축.
 *
 * ### MA02(`ma17`)와 무엇이 다른가
 *
 * | | MA02 | MA18 |
 * |---|---|---|
 * | 원장 | `master_item` | `sku_attribute` |
 * | 한 행 | 상품 옵션 | **SKU 하나** (옵션으로 불어나지 않는다) |
 * | 화주 범위 | 전 유형 | **MWP·MLT 만** |
 * | 등록 | `POST` 행별 | **`POST /bulk` 청크(1,000)** — SCM Hub 채번 |
 * | 삭제 | 있음 | **없음** (LLD §5.7) |
 * | 바코드 수정 | 가능 | **불가** (채번 키 — LLD §5.6) |
 *
 * ### 지금은 `MASTER` 전용이다
 *
 * BE 네 엔드포인트가 `hasAuthority('MASTER')` 로 임시 축소돼 있다 (LOGISPRD-13284). 공통 화주
 * 소유 가드(LOGISPRD-12489)가 `main` 에 들어오면 되돌린다. 그때까지 MWP·MLT 담당자는 **403** 이라,
 * 테이블이 그 응답을 빈 목록이 아니라 **메시지로** 드러낸다.
 */
function SkuAttributeManagement() {
  const { ready, coMingle } = useCoMingleShipperScope()
  const scope = useMemo(() => withCoMingle(shipperScopeOf(AuthStorage.user.get()), coMingle), [coMingle])
  // 조회 조건을 메뉴 세션·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다 (MOMS 공통 UX)
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()
  // 마운트 시 한 번만 복원한다 — 이후 조회로 URL 이 바뀌어도 입력을 덮어쓰지 않는다
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const restoredParams = useMemo<SkuAttributeParams | undefined>(() => fromQuery(parsedQs), [])

  // 조회 조건과 페이지 — 다른 메뉴 탭에서 돌아왔으면 보던 목록부터 연다 (LOGISPRD-14923, useListView KDoc)
  const persistCondition = useCallback(
    (params: SkuAttributeParams) => {
      MenuStorage.menuOnSession.setSearch(params)
      reload()
      replaceQuery(params)
    },
    [reload, replaceQuery],
  )
  const { view, onSearch, onInitialSearch, onPageChange, onReset } = useListView({ restoredParams, persistCondition })
  // 필터에서 지금 고른 화주(조회 전 포함) — 조회 전에도 그 화주로 등록을 연다 (LOGISPRD-14591)
  const [selectedShipperCode, setSelectedShipperCode] = useState<string>()

  // 화주 옵션 — 훅은 queries/ 에 둔다 (CLAUDE.md 서비스 레이어). MWP·MLT 두 유형만 묻는 근거와
  // staleTime 을 고정한 근거는 그 훅 KDoc 에 있다
  const { data: shipperOptionsResult, isPending: isShipperOptionsPending, isError: isShipperOptionsError } = useGetShipperOptions()
  const shippers = shipperOptionsResult?.items ?? NO_SHIPPERS

  // **일부 유형만 실패하면 알린다.** 성공분만 조용히 보여 주면 사용자는 「그 유형 화주가 없구나」로
  // 읽는다 — 큰 실패가 조용한 누락으로 바뀐다
  const failedServiceTypes = shipperOptionsResult?.failedServiceTypes
  useEffect(() => {
    if (failedServiceTypes?.length) {
      message.warning(`${failedServiceTypes.join('·')} 화주 목록을 가져오지 못했습니다. 해당 유형은 조회할 수 없습니다.`)
    }
  }, [failedServiceTypes])

  /**
   * 필터에 넘길 **원본 목록**. 옵션 가공은 필터가 공용 헬퍼로 한다.
   *
   * `ShipperOptionSource` 의 유형 필드명이 `logisticsServiceType` 이라 이름만 맞춰 준다 —
   * 이 화면의 BE 응답은 `serviceType` 이다.
   */
  const shipperSources: ShipperOptionSource[] = useMemo(
    () => shippers.map((s) => ({ shipperCode: s.shipperCode, shipperName: s.shipperName, logisticsServiceType: s.serviceType })),
    [shippers],
  )
  const shipperInfo: ShipperInfoMap = useMemo(
    () => Object.fromEntries(shippers.map((s) => [s.shipperCode, { shipperName: s.shipperName, serviceType: s.serviceType }])),
    [shippers],
  )

  return (
    <ConfigProvider theme={pboLogisticsTheme}>
      {/* 좌우 패딩은 앱 셸이 24px 를 이미 제공 — 추가 패딩 금지 (MA02 와 같은 규칙) */}
      <div className="pbo-core-screen" style={{ paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <PageHeader title="상품속성관리 (SKU)" />
        <SkuAttributeFilter
          scope={scope}
          shippers={shipperSources}
          shippersReady={ready && !isShipperOptionsPending}
          coMingle={coMingle}
          initialParams={restoredParams}
          onSearch={onSearch}
          onInitialSearch={onInitialSearch}
          onReset={onReset}
          onShipperSelect={setSelectedShipperCode}
        />
        <SkuAttributeTable
          scope={scope}
          searchParams={view.params}
          page={view.page}
          onPageChange={onPageChange}
          shipperInfo={shipperInfo}
          // 등록 화주유형 가드는 목록이 완전할 때만 판정한다 (LOGISPRD-14586 — registrationBlockReason KDoc)
          shipperOptionsComplete={!isShipperOptionsPending && !isShipperOptionsError && !failedServiceTypes?.length}
          selectedShipperCode={selectedShipperCode}
        />
      </div>
    </ConfigProvider>
  )
}

export default SkuAttributeManagement
